/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('hero-slideshow')) return;

  document.addEventListener('click', function (e) {
    var card = e.target && e.target.closest && e.target.closest('[data-hero-curator]');
    if (!card) return;
    var launcher = document.querySelector('[data-curator-launcher]');
    if (launcher) launcher.click();
  });

  var setHeroOffset = function () {
    var ticker = document.querySelector('.announcement-ticker');
    var h = ticker ? Math.ceil(ticker.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--hero-top-offset', h + 'px');
  };
  setHeroOffset();
  window.addEventListener('resize', setHeroOffset);
  window.addEventListener('load', setHeroOffset);

  var motionOn = function () {
    return !!(window.gsap && document.documentElement.classList.contains('motion'));
  };

  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      if (!this.slides.length) return;

      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.progressBar = this.querySelector('[data-hero-progress]');
      this.toggleBtn = this.querySelector('[data-hero-toggle]');
      this.liveRegion = this.querySelector('[data-hero-live]');
      this.strings = this.readStrings();

      this.index = 0;
      this.animating = false;
      this.userPaused = false;
      this.hovered = false;
      this.inView = true;
      this.duration = (parseFloat(this.dataset.duration) || 7) * 1000;
      this.autoplay =
        this.dataset.autoplay === 'true' &&
        this.slides.length > 1 &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.kenBurnsTweens = [];
      this.split = null;

      this.bindControls();
      this.observeViewport();

      this.slides.forEach(function (slide, i) {
        if (i !== 0) {
          slide.inert = true;
          slide.querySelectorAll('video').forEach(function (v) { v.pause(); });
        }
      });

      var self = this;
      if (motionOn()) {
        document.fonts.ready.then(function () { self.entrance(); });
      } else if (this.progressBar) {
        this.progressBar.style.display = 'none';
      }

      this.startTimer();
    }

    disconnectedCallback() {
      this.stopTimer();
      this.killKenBurns();
      if (this.split) { try { this.split.revert(); } catch (e) {} }
    }

    readStrings() {
      var defaults = { pause: 'Pause slideshow', play: 'Play slideshow', slideOf: 'Slide [index] of [count]' };
      var el = this.querySelector('[data-hero-strings]');
      if (!el) return defaults;
      try { return Object.assign(defaults, JSON.parse(el.textContent)); } catch (e) { return defaults; }
    }

    bindControls() {
      var self = this;
      var prev = this.querySelector('[data-hero-prev]');
      var next = this.querySelector('[data-hero-next]');
      if (prev) prev.addEventListener('click', function () { self.goTo(self.index - 1); });
      if (next) next.addEventListener('click', function () { self.goTo(self.index + 1); });

      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { self.goTo(i); });
      });

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', function () {
          self.userPaused = !self.userPaused;
          self.toggleBtn.setAttribute('aria-pressed', String(self.userPaused));
          var label = self.toggleBtn.querySelector('[data-hero-toggle-label]');
          if (label) label.textContent = self.userPaused ? self.strings.play : self.strings.pause;
          self.userPaused ? self.pauseTimer() : self.resumeTimer();
        });
      }

      this.addEventListener('mouseenter', function () { self.hovered = true; self.pauseTimer(); });
      this.addEventListener('mouseleave', function () { self.hovered = false; self.resumeTimer(); });
      this.addEventListener('focusin', function () { self.hovered = true; self.pauseTimer(); });
      this.addEventListener('focusout', function (e) {
        if (!self.contains(e.relatedTarget)) { self.hovered = false; self.resumeTimer(); }
      });

      document.addEventListener('visibilitychange', function () {
        document.hidden ? self.pauseTimer() : self.resumeTimer();
      });

      var startX = null;
      this.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 48) self.goTo(dx < 0 ? self.index + 1 : self.index - 1);
        startX = null;
      }, { passive: true });
    }

    observeViewport() {
      var self = this;
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(function (entries) {
        self.inView = entries[0].isIntersecting;
        if (self.inView) {
          self.resumeTimer();
          self.activeVideo() && self.activeVideo().play().catch(function () {});
        } else {
          self.pauseTimer();
          self.slides.forEach(function (s) {
            s.querySelectorAll('video').forEach(function (v) { v.pause(); });
          });
        }
      }, { threshold: 0.1 }).observe(this);
    }

    activeVideo() {
      return this.slides[this.index].querySelector('video');
    }

    startTimer() {
      if (!this.autoplay) return;
      this.stopTimer();
      var self = this;
      if (motionOn() && this.progressBar) {
        this.progressTween = window.gsap.fromTo(
          this.progressBar,
          { scaleX: 0 },
          { scaleX: 1, duration: this.duration / 1000, ease: 'none', onComplete: function () { self.goTo(self.index + 1); } }
        );
        if (this.userPaused || this.hovered || !this.inView) this.progressTween.pause();
      } else {
        this.timerStarted = Date.now();
        this.timerRemaining = this.duration;
        if (!(this.userPaused || this.hovered || !this.inView)) {
          this.timer = setTimeout(function () { self.goTo(self.index + 1); }, this.duration);
        }
      }
    }

    stopTimer() {
      if (this.progressTween) { this.progressTween.kill(); this.progressTween = null; }
      if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    }

    pauseTimer() {
      if (this.progressTween) this.progressTween.pause();
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
        this.timerRemaining -= Date.now() - this.timerStarted;
      }
    }

    resumeTimer() {
      if (this.userPaused || this.hovered || !this.inView) return;
      if (this.progressTween) this.progressTween.play();
      else if (this.autoplay && !this.timer && !motionOn()) {
        var self = this;
        this.timerStarted = Date.now();
        this.timer = setTimeout(function () { self.goTo(self.index + 1); }, Math.max(this.timerRemaining || this.duration, 500));
      }
    }

    killKenBurns() {
      this.kenBurnsTweens.forEach(function (t) { t.kill(); });
      this.kenBurnsTweens = [];
    }

    kenBurns(slide) {
      if (!motionOn()) return;
      var media = slide.querySelector('[data-hero-media]');
      if (!media) return;
      this.killKenBurns();
      this.kenBurnsTweens.push(
        window.gsap.fromTo(media, { scale: 1 }, { scale: 1.08, duration: this.duration / 1000 + 1.6, ease: 'none' })
      );
    }

    animateContent(slide, tl) {
      var gsap = window.gsap;
      var heading = slide.querySelector('[data-hero-heading]');
      var items = slide.querySelectorAll('[data-hero-item]');

      if (heading && window.SplitText) {
        if (this.split) { try { this.split.revert(); } catch (e) {} }
        this.split = new window.SplitText(heading, { type: 'lines,chars', linesClass: 'split-line', mask: 'lines' });
        tl.fromTo(
          this.split.chars,
          { yPercent: 110 },
          { yPercent: 0, duration: 0.8, ease: 'expo.out', stagger: 0.015 },
          0.15
        );
      } else if (heading) {
        tl.fromTo(heading, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out' }, 0.15);
      }
      if (items.length) {
        tl.fromTo(
          items,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', stagger: 0.08, clearProps: 'opacity,transform' },
          0.3
        );
      }
    }

    entrance() {
      if (!motionOn()) return;
      var tl = window.gsap.timeline();
      this.animateContent(this.slides[0], tl);
      this.kenBurns(this.slides[0]);
    }

    goTo(nextIndex) {
      var count = this.slides.length;
      if (count < 2) return;
      nextIndex = ((nextIndex % count) + count) % count;
      if (nextIndex === this.index || this.animating) return;

      var outgoing = this.slides[this.index];
      var incoming = this.slides[nextIndex];
      this.index = nextIndex;

      this.dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === nextIndex ? 'true' : 'false');
      });
      this.slides.forEach(function (slide, i) {
        slide.inert = i !== nextIndex;
        if (i === nextIndex) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      if (this.liveRegion) {
        this.liveRegion.textContent = this.strings.slideOf
          .replace('[index]', nextIndex + 1)
          .replace('[count]', count);
      }

      var inVideo = incoming.querySelector('video');
      if (inVideo) inVideo.play().catch(function () {});

      this.stopTimer();

      if (motionOn()) {
        var self = this;
        var gsap = window.gsap;
        this.animating = true;

        incoming.classList.add('is-active');
        gsap.set(incoming, { zIndex: 2, opacity: 1, visibility: 'visible', clipPath: 'inset(100% 0% 0% 0%)' });
        gsap.set(outgoing, { zIndex: 1 });

        var tl = gsap.timeline({
          onComplete: function () {
            outgoing.classList.remove('is-active');
            gsap.set(outgoing, { clearProps: 'zIndex,opacity,visibility' });
            gsap.set(incoming, { clearProps: 'clipPath,zIndex' });
            outgoing.querySelectorAll('video').forEach(function (v) { v.pause(); });
            self.animating = false;
            self.startTimer();
          },
        });

        tl.fromTo(incoming, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.0, ease: 'expo.out' }, 0);
        tl.to(outgoing, { opacity: 0.35, duration: 1.0, ease: 'power2.out' }, 0);
        this.animateContent(incoming, tl);
        this.kenBurns(incoming);
      } else {
        outgoing.classList.remove('is-active');
        incoming.classList.add('is-active');
        outgoing.querySelectorAll('video').forEach(function (v) { v.pause(); });
        this.startTimer();
      }
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
})();
